import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { ShieldStar, ArrowRight, CheckCircle, Warning, Eye } from "@phosphor-icons/react";

const OSINT_MAP = "https://static.prod-images.emergentagent.com/jobs/4545735e-77e3-490b-85fb-3bc856dbec8e/images/2363b00f350c042171179770774587cb8ad0f3e73528cbf014a1bed124e9cfe2.png";

export default function HealthScore() {
  const nav = useNavigate();
  const [form, setForm] = useState({ company_name: "", industry: "", country: "IN", contact_email: "", contact_name: "" });
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const { data } = await api.post("/public/health-score", form);
      setResult(data);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || "Could not generate report.");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="health-score-page">
      {/* Top bar */}
      <header className="border-b border-[var(--span-border)] px-6 lg:px-12 py-4 flex items-center justify-between">
        <img src="/api/static/brand/C2_Horizontal_White.svg" alt="SPAN CasePrime" style={{ width: "260px", height: "auto" }} />
        <button onClick={() => nav("/login")} className="span-btn span-btn-ghost h-9 text-[12px]">Operator login</button>
      </header>

      {!result ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 min-h-[calc(100vh-65px)]">
          <div className="px-6 lg:px-16 py-12 flex flex-col justify-center max-w-[640px]">
            <div className="label-eyebrow mb-4">Free Brand Exposure Report · 60 seconds</div>
            <h1 className="font-display text-[44px] font-bold tracking-tight leading-[1.02]">
              How exposed is your brand,<br/>
              <span className="text-[var(--span-text-2)]">really?</span>
            </h1>
            <p className="text-[14px] text-[var(--span-text-2)] mt-4 leading-relaxed max-w-[520px]">
              SPAN's IntelliProbe v3.0 scans tier-1 marketplaces, social platforms, the dark web,
              domain registries and customs records for signals of counterfeiting, impersonation, and
              IP violation against your brand. Get a confidential exposure index in under a minute.
            </p>

            <div className="grid grid-cols-3 gap-px bg-[var(--span-border)] mt-8 mb-8 border border-[var(--span-border)]">
              {[
                { k: "Brands scored", v: "120+" },
                { k: "Counterfeit seizures", v: "$48M" },
                { k: "Jurisdictions", v: "9" },
              ].map((m) => (
                <div key={m.k} className="bg-white p-4">
                  <div className="label-eyebrow">{m.k}</div>
                  <div className="font-display text-[26px] font-bold mt-1 tnum">{m.v}</div>
                </div>
              ))}
            </div>

            <form onSubmit={submit} className="space-y-3" data-testid="health-score-form">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label-eyebrow block mb-1.5">Company name *</label>
                  <input required data-testid="hs-company" value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} className="span-input" placeholder="e.g., Marico Limited" />
                </div>
                <div>
                  <label className="label-eyebrow block mb-1.5">Industry</label>
                  <input value={form.industry} onChange={(e) => setForm({ ...form, industry: e.target.value })} className="span-input" placeholder="e.g., FMCG" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label-eyebrow block mb-1.5">Country</label>
                  <select value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} className="span-input">
                    {["IN", "BD", "PK", "SG", "AE", "EG", "UK", "US", "CH"].map(c => <option key={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="label-eyebrow block mb-1.5">Your name</label>
                  <input value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} className="span-input" placeholder="optional" />
                </div>
              </div>
              <div>
                <label className="label-eyebrow block mb-1.5">Work email *</label>
                <input required type="email" data-testid="hs-email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} className="span-input font-mono" placeholder="you@company.com" />
              </div>
              {err && <div className="text-[12px] text-[var(--span-danger)]">{err}</div>}
              <button data-testid="hs-submit" disabled={busy} className="span-btn w-full disabled:opacity-50">
                {busy ? "Scanning OSINT signals…" : "Get my exposure score"} <ArrowRight size={14}/>
              </button>
              <p className="text-[10px] text-[var(--span-text-2)] font-mono mt-2">
                Confidential. Generated by SPAN. ISO 27035 compliant chain of custody applies if you escalate to a case.
              </p>
            </form>
          </div>
          <div className="hidden lg:block relative bg-black">
            <img src={OSINT_MAP} alt="" className="absolute inset-0 w-full h-full object-cover opacity-90" />
            <div className="absolute inset-0 bg-gradient-to-tr from-black/40 via-transparent to-black/30" />
            <div className="absolute bottom-12 left-12 right-12 text-white">
              <ShieldStar size={28} weight="bold" />
              <div className="font-mono text-[11px] tracking-[0.18em] uppercase opacity-80 mt-3">
                INTELLIPROBE v3.0 · LIVE THREAT FEED
              </div>
              <div className="font-display font-bold text-[36px] leading-[1.05] mt-2 max-w-md">
                We've seen this before. We'll find yours too.
              </div>
            </div>
          </div>
        </div>
      ) : (
        <ResultView result={result} onAnother={() => { setResult(null); setForm({ ...form, company_name: "" }); }} />
      )}
    </div>
  );
}

function ResultView({ result, onAnother }) {
  const riskColor = { critical: "var(--span-danger)", high: "var(--span-danger)", medium: "var(--span-warning)", low: "var(--span-success)" }[result.risk_level];
  return (
    <div className="px-6 lg:px-16 py-12 max-w-[1100px] mx-auto" data-testid="health-score-result">
      <div className="label-eyebrow">SPAN Brand Exposure Report</div>
      <h1 className="font-display text-[36px] font-bold tracking-tight mt-2">
        {result.company_name}
      </h1>
      <div className="text-[13px] text-[var(--span-text-2)] mt-1">{result.industry || "—"} · {result.country} · generated just now</div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-8">
        <div className="card-flat p-6 lg:col-span-1" style={{ borderColor: riskColor }}>
          <div className="label-eyebrow">Exposure index</div>
          <div className="flex items-baseline gap-2 mt-2">
            <span className="font-display font-bold text-[72px] tnum leading-none" style={{ color: riskColor }}>{result.exposure_index}</span>
            <span className="text-[var(--span-text-2)] font-mono text-[12px]">/100</span>
          </div>
          <span className="badge font-display font-bold text-[12px] mt-3" style={{ background: riskColor, color: "white", borderColor: riskColor }}>
            {result.risk_level.toUpperCase()} RISK
          </span>
          <p className="text-[12px] text-[var(--span-text-2)] mt-4 leading-relaxed">
            Higher index = greater exposure. Calculated from 6 sub-signals across marketplace,
            social, dark web, counterfeit clusters, domain squatting and chain-of-custody readiness.
          </p>
        </div>

        <div className="card-flat p-6 lg:col-span-2">
          <div className="label-eyebrow mb-4">Sub-signal breakdown</div>
          <div className="space-y-3">
            {Object.entries(result.sub_scores).map(([k, v]) => (
              <div key={k}>
                <div className="flex items-center justify-between text-[12px] mb-1">
                  <span className="capitalize">{k.replace(/_/g, " ")}</span>
                  <span className="font-mono font-semibold tnum">{v}/100</span>
                </div>
                <div className="h-1.5 bg-[var(--span-surface-2)] relative">
                  <div className="absolute inset-y-0 left-0" style={{ width: `${v}%`, background: v < 50 ? "var(--span-danger)" : v < 70 ? "var(--span-warning)" : "var(--span-success)" }}></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
        <div className="card-flat p-6">
          <div className="label-eyebrow flex items-center gap-1"><Warning size={11}/> Detected signals</div>
          <ul className="mt-3 space-y-2">
            {result.findings.map((f, i) => (
              <li key={i} className="text-[13px] flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full mt-2 shrink-0" style={{ background: riskColor }}></span>
                {f}
              </li>
            ))}
          </ul>
        </div>
        <div className="card-flat p-6">
          <div className="label-eyebrow flex items-center gap-1"><CheckCircle size={11}/> SPAN recommendations</div>
          <ul className="mt-3 space-y-2">
            {result.recommendations.map((r, i) => (
              <li key={i} className="text-[13px] flex items-start gap-2">
                <span className="font-mono text-[10px] text-[var(--span-text-2)] w-5 shrink-0">0{i+1}</span>
                {r}
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="card-flat mt-4 p-6 lg:flex items-center justify-between gap-6">
        <div>
          <div className="label-eyebrow">Next step</div>
          <div className="font-display font-bold text-[20px] mt-1">Open a confidential brand protection case.</div>
          <div className="text-[13px] text-[var(--span-text-2)] mt-1">A SPAN case manager will reach out within 24 hours to {result.contact_email}.</div>
        </div>
        <div className="flex items-center gap-2 mt-4 lg:mt-0">
          <button onClick={onAnother} className="span-btn span-btn-ghost" data-testid="hs-score-another">Score another brand</button>
          <a href="mailto:pankaj@span.com?subject=SPAN brand protection enquiry" className="span-btn" data-testid="hs-talk-to-span">
            <Eye size={13}/> Talk to SPAN
          </a>
        </div>
      </div>

      {/* Self-serve conversion CTA — Pro plan ₹99,000/mo */}
      <div className="mt-6 border-2 border-black bg-black text-white p-8 lg:p-10" data-testid="hs-pro-cta">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 items-center">
          <div className="lg:col-span-3">
            <div className="label-eyebrow opacity-70" style={{color: "#fff"}}>Stop counterfeiters in 24 hours</div>
            <div className="font-display font-bold text-[28px] lg:text-[34px] leading-[1.05] mt-2">
              Skip the wait — go live with SPAN Pro today.
            </div>
            <ul className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-[13px] opacity-90">
              {[
                "Continuous IntelliProbe v3.0 scan",
                "Up to 50 active cases",
                "Court-admissible evidence bundles",
                "Live OSINT signals & escalations",
                "9-currency dual-track invoicing",
                "14-day money-back trial",
              ].map((b) => (
                <li key={b} className="flex items-start gap-2">
                  <CheckCircle size={14} weight="bold" className="shrink-0 mt-0.5" />{b}
                </li>
              ))}
            </ul>
          </div>
          <div className="lg:col-span-2 flex flex-col items-start lg:items-end">
            <div className="label-eyebrow opacity-70" style={{color: "#fff"}}>SPAN Pro</div>
            <div className="font-display font-bold text-[44px] tnum leading-none mt-1">₹99,000<span className="text-[14px] font-mono opacity-70 ml-1">/mo</span></div>
            <div className="text-[11px] font-mono opacity-70 mt-1">Billed monthly · cancel anytime</div>
            <a
              href={`/login?next=/billing&prefill_email=${encodeURIComponent(result.contact_email || "")}&prefill_company=${encodeURIComponent(result.company_name || "")}`}
              className="span-btn mt-5 w-full lg:w-auto"
              style={{ background: "#fff", color: "#000", borderColor: "#fff" }}
              data-testid="hs-start-trial"
            >
              Start my 14-day trial <ArrowRight size={14} />
            </a>
            <div className="text-[10px] font-mono opacity-60 mt-2">No credit card needed to view your full dossier first.</div>
          </div>
        </div>
      </div>


      <p className="text-[10px] text-[var(--span-text-2)] font-mono mt-8 max-w-2xl">
        This report is generated from SPAN IntelliProbe v3.0 OSINT models. Exposure index is illustrative —
        a confidential, full-spectrum assessment is included when you open a case with SPAN Consulting.
        © SPAN · ISO 27035 chain of custody.
      </p>
    </div>
  );
}
